import { FC, useCallback, useMemo } from 'react'

import { IChangeTreeSelectFn, TreeSelect } from 'cng-common/components/treeSelect'

import { useProductTreeItems } from './hooks/useProductTreeItems'
import { IMappedProductTreeItem } from './models/mappedProductTreeItem'
import { IProductTreeProps } from './models/productTreeProps'

export const ProductTree: FC<IProductTreeProps> = ({
  values = [],
  label,
  className,
  popupClassName,
  onSelectChange,
  root,
  disabled,
  fYear,
  typeOfSelectedNodes,
  partnerType,
  extendedItems,
  programType,
}) => {
  /** -------------------- Hooks ----------------------- */

  const productTreeItems = useProductTreeItems({ fYear, root, partnerType, programType })

  /** -------------------- Variables ----------------------- */

  const productTreeItemsExtended = useMemo(() => {
    return !extendedItems?.length
      ? productTreeItems
      : [
          ...productTreeItems,
          ...extendedItems.map((el) => ({ ...el, parentId: root ?? el.parentId })),
        ]
  }, [extendedItems, productTreeItems, root])

  /** -------------------- Methods ----------------------- */

  const handleChange = useCallback<IChangeTreeSelectFn<IMappedProductTreeItem>>(
    (displayedItems, allSelectedItems, onlyLeaves) => {
      const mapById = (item: IMappedProductTreeItem) => item.itemId
      onSelectChange(
        displayedItems.map(mapById),
        allSelectedItems.map(mapById),
        onlyLeaves.map(mapById)
      )
    },
    [onSelectChange]
  )

  const handleSearch = useCallback((item: IMappedProductTreeItem, searchValue: string) => {
    const fieldsToSearch = [item.itemId, item.name].filter((sf): sf is string => Boolean(sf))
    return fieldsToSearch.some((value) => value.toLowerCase().includes(searchValue.toLowerCase()))
  }, [])

  /** -------------------- Render ----------------------- */

  return (
    <div className={className}>
      <TreeSelect
        values={values}
        label={label}
        popupClassName={popupClassName}
        data={productTreeItemsExtended}
        itemCaption={'name'}
        onChange={handleChange}
        onSearch={handleSearch}
        disabled={disabled}
        typeOfSelectedNodes={typeOfSelectedNodes}
      />
    </div>
  )
}
